import { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";
import MileageUpdateAddress from "../../types/mileage-update-address";
import { MileageUpdateAddressRouteItem } from "./mileage-update-address-routes.types";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import mileageTranslationsHelper from "../../../../../../languages/mileage-translations.helper";
import useMileageAddressList from "../../../../../../common/services/mileage/address/list/use-mileage-address-list";
import useAbort from "../../../../../../common/hooks/use-abort";
import { useAppContext } from "../../../../../../context/app.context";
import useOpen from "../../../../../../common/hooks/use-open";
import { debounce } from "lodash";
import MileageAddressListParams from "../../../../../../common/services/mileage/address/list/mileage-address-list-params";
import MileageUpdateAddressSelectOption from "../../types/mileage-update-address-select-option";
import SelectOption from "../../../../../../common/components/form/select/common/option/select-option";
import mileageUpdateAddressFactory from "../../factory/mileage-update-address.factory";
import MileageAddressAddModalAddress from "../../../../common/routes/address-add-modal/types/address-add-modal-address";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical, faTrash } from "@fortawesome/free-solid-svg-icons";
import SingleSelectComponent from "../../../../../../common/components/form/select/single-select/single-select.component";
import ButtonComponent from "../../../../../../common/components/button/button.component";
import MileageAddressAddModalComponent from "../../../../common/routes/address-add-modal/address-add-modal.component";

type MileageUpdateAddressRouteItemProps = {
  routeIndex: number;
  routeItem: MileageUpdateAddressRouteItem;
  updateRouteItem: (updatedRouteItem: MileageUpdateAddressRouteItem) => void;
  deleteRouteItem: () => void;
  cargoCompanyAddresses: MileageUpdateAddress[];
  additionalAddresses: MileageUpdateAddress[];
  dragHandleProps: DraggableProvidedDragHandleProps | null | undefined;
  onBlur?: () => void;
  idForTesting?: string;
};

const MileageUpdateAddressRouteItemComponent: FC<
  MileageUpdateAddressRouteItemProps
> = (props) => {
  const translations =
    mileageTranslationsHelper.getMileageUpdateTranslations().routes;
  const mileageAddressList = useMileageAddressList();
  const mileageAddressListAbort = useAbort();

  const [addressSearchQuery, setAddressSearchQuery] = useState("");

  const { selectedAppLanguage } = useAppContext();
  const { isOpen, open, close } = useOpen();

  const loadAddressesDebounced = useCallback(
    debounce((addressSearchQuery: string) => {
      const loadParams: MileageAddressListParams = {
        searchQuery: addressSearchQuery,
      };

      mileageAddressList.load(loadParams, mileageAddressListAbort.signal);
    }, 500),
    [],
  );

  useEffect(() => {
    if (!addressSearchQuery) {
      return;
    }

    loadAddressesDebounced(addressSearchQuery);

    return () => mileageAddressListAbort.revoke();
  }, [addressSearchQuery]);

  const addressSelectOptions = useMemo<
    MileageUpdateAddressSelectOption[]
  >(() => {
    if (!addressSearchQuery) {
      const additionalAddress: SelectOption = {
        label: translations.addressAddLabel,
        value: null,
        onClick: open,
      };
      return [additionalAddress];
    }

    const cargoCompanyAddressListOptions: MileageUpdateAddressSelectOption[] =
      props.cargoCompanyAddresses
        .filter((address) =>
          address.displayName
            .toLowerCase()
            .includes(addressSearchQuery.toLowerCase()),
        )
        .map(mileageUpdateAddressFactory.createAddressSelectOption);

    const passengerOptions = props.additionalAddresses
      .filter((address: MileageUpdateAddress) => {
        return address.displayName
          .toLocaleLowerCase()
          .includes(addressSearchQuery.toLocaleLowerCase());
      })
      .map((value) =>
        mileageUpdateAddressFactory.createAddressSelectOption(value),
      );

    const addressListOptions: MileageUpdateAddressSelectOption[] =
      mileageAddressList.data.map(
        mileageUpdateAddressFactory.createAddressSelectOptionFromMileageAddressListItem,
      );

    return [
      ...passengerOptions,
      ...cargoCompanyAddressListOptions,
      ...addressListOptions,
    ];
  }, [
    props.additionalAddresses,
    mileageAddressList.data,
    props.cargoCompanyAddresses,
    mileageAddressList.isLoading,
    addressSearchQuery,
    selectedAppLanguage,
  ]);

  const addressSelectOption = props.routeItem.address
    ? mileageUpdateAddressFactory.createAddressSelectOption(
        props.routeItem.address,
      )
    : null;

  const isAddressListLoading =
    !!addressSearchQuery && mileageAddressList.isLoading;

  const onAddressChange = (address: MileageUpdateAddress | null) => {
    const item: MileageUpdateAddressRouteItem = {
      uuid: props.routeItem.uuid,
      address: address,
    };

    props.updateRouteItem(item);
  };

  const onAddressAdd = (address: MileageAddressAddModalAddress) => {
    const updateAddress: MileageUpdateAddress = {
      displayName: `${address.zipCode}, ${address.town}, ${address.street} ${address.houseNumber}`,
      latitude: address.latitude,
      longitude: address.longitude,
      apartmentNumber: address.apartmentNumber,
      houseNumber: address.houseNumber,
      street: address.street,
      town: address.town,
      zipCode: address.zipCode,
    };

    const item: MileageUpdateAddressRouteItem = {
      uuid: props.routeItem.uuid,
      address: updateAddress,
    };

    props.updateRouteItem(item);
  };

  return (
    <div className="mileage_update_address_route_list_item">
      <div className="mileage_update_address_route_list_item__top_row">
        <button
          className="mileage_update_address_route_list_item__drag_button"
          title={translations.dragButtonTitle}
          {...props.dragHandleProps}
        >
          <FontAwesomeIcon icon={faEllipsisVertical} />
        </button>
        <div className="mileage_update_address_route_list_item__index">
          {props.routeIndex + 1}
        </div>
        <SingleSelectComponent
          onChange={(value) => onAddressChange(value?.value)}
          options={addressSelectOptions}
          defaultValue={addressSelectOption}
          value={addressSelectOption}
          placeholder={translations.selectPlaceholder}
          isLoading={isAddressListLoading}
          isDisabled={mileageAddressList.isError}
          maxMenuHeight={500}
          isSearchable
          filterFunction={() => true}
          inputValue={addressSearchQuery}
          onInputChange={setAddressSearchQuery}
          isClearable
          onBlur={props.onBlur}
          noOptionsMessage={(inputValue) => {
            if (inputValue) {
              return translations.searchNoOptionsMessage;
            }
            return translations.searchTipMessage;
          }}
          idForTesting={props.idForTesting}
          classNames={{
            root: "mileage_update_address_route_list_item__address",
          }}
        />
        <ButtonComponent
          onClick={props.deleteRouteItem}
          classNames={{
            root: "mileage_update_address_route_list_item__action_button",
          }}
          idForTesting={`mileage-update-address-route-list-item-${props.routeItem.uuid}-delete-button`}
        >
          <FontAwesomeIcon icon={faTrash} />
        </ButtonComponent>
      </div>
      <MileageAddressAddModalComponent
        isOpen={isOpen}
        onClose={close}
        onAddNewAddress={onAddressAdd}
      />
    </div>
  );
};

export default MileageUpdateAddressRouteItemComponent;
